/**
 * ComponentCatalog — pre-approved component set for the declarative module renderer.
 *
 * Every component here is in the ADR-022 frozen catalog. Unknown components render
 * a visible placeholder rather than crashing.
 *
 * Bindings: prop values of the form "{{source.path}}" are resolved by renderNode
 * against the fetched data bag before the component receives them.
 */
import type { CSSProperties, ReactNode } from 'react'
import { themeColor, useTheme } from '../theme/theme'
import type { ResolvedTheme } from '../theme/theme'
import {
  LineChart as RechartsLine,
  BarChart as RechartsBar,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

// ---------------------------------------------------------------------------
// Layout components
// ---------------------------------------------------------------------------

export function Page({ children }: { children?: ReactNode }) {
  return <div className="space-y-6">{children}</div>
}

export function Section({ title, children }: { title?: string; children?: ReactNode }) {
  return (
    <section className="space-y-4">
      {title && <h2 className="text-base font-semibold text-gray-800">{title}</h2>}
      {children}
    </section>
  )
}

export function Grid({ columns = 2, children }: { columns?: number; children?: ReactNode }) {
  const colClass: Record<number, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-3',
    4: 'grid-cols-2 sm:grid-cols-4',
  }
  const cls = colClass[columns] ?? 'grid-cols-2'
  return <div className={`grid gap-4 ${cls}`}>{children}</div>
}

export function Stack({ gap = 4, children }: { gap?: number; children?: ReactNode }) {
  const gapClass = `gap-${Math.min(gap, 12)}`
  return <div className={`flex flex-col ${gapClass}`}>{children}</div>
}

export function Card({ title, children }: { title?: string; children?: ReactNode }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-3">
      {title && <h3 className="text-sm font-semibold text-gray-700">{title}</h3>}
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Display components
// ---------------------------------------------------------------------------

export function Heading({ level = 2, text }: { level?: number; text?: string }) {
  const Tag = (`h${Math.min(Math.max(level, 1), 6)}`) as keyof JSX.IntrinsicElements
  const sizeClass: Record<number, string> = {
    1: 'text-2xl font-bold text-gray-900',
    2: 'text-xl font-semibold text-gray-900',
    3: 'text-lg font-semibold text-gray-800',
    4: 'text-base font-semibold text-gray-700',
    5: 'text-sm font-semibold text-gray-700',
    6: 'text-xs font-semibold text-gray-600',
  }
  return <Tag className={sizeClass[level] ?? sizeClass[2]}>{text ?? ''}</Tag>
}

export function Text({ text }: { text?: string }) {
  return <p className="text-sm text-gray-700">{text ?? ''}</p>
}

function formatStat(value: unknown, format?: string): string {
  if (value === null || value === undefined) return '—'
  if (format === 'pct') return `${(Number(value) * 100).toFixed(1)}%`
  if (format === 'int') return String(Math.round(Number(value)))
  if (format === 'float') return Number(value).toFixed(2)
  return String(value)
}

export function Stat({
  label,
  value,
  delta,
  format,
}: {
  label?: string
  value?: unknown
  delta?: unknown
  format?: string
}) {
  const deltaNum = delta !== null && delta !== undefined ? Number(delta) : null
  return (
    <div>
      {label && <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>}
      <p className="text-2xl font-bold text-gray-900 mt-1">{formatStat(value, format)}</p>
      {deltaNum !== null && (
        <p className={`text-xs mt-0.5 ${deltaNum >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {deltaNum >= 0 ? '+' : ''}{deltaNum.toFixed(1)}
        </p>
      )}
    </div>
  )
}

export function Table({
  columns,
  rows,
}: {
  columns?: Array<{ key: string; label: string }>
  rows?: Array<Record<string, unknown>>
}) {
  if (!columns || columns.length === 0) {
    return <p className="text-xs text-gray-400 italic">No columns defined.</p>
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="bg-gray-50 border-b border-gray-100">
          <tr>
            {columns.map(col => (
              <th key={col.key} className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {(rows ?? []).map((row, i) => (
            <tr key={i} className="hover:bg-gray-50">
              {columns.map(col => (
                <td key={col.key} className="px-3 py-2 text-gray-700">
                  {String(row[col.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
          {(!rows || rows.length === 0) && (
            <tr>
              <td colSpan={columns.length} className="px-3 py-6 text-center text-gray-400 text-xs">
                No data.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

function tooltipStyle(resolved: ResolvedTheme): CSSProperties {
  return {
    backgroundColor: themeColor('--mc-surface'),
    ...(resolved === 'dark' ? { border: `1px solid ${themeColor('--mc-gray-200')}` } : {}),
  }
}

export function LineChart({
  data,
  xKey,
  yKey,
  title,
}: {
  data?: unknown[]
  xKey?: string
  yKey?: string
  title?: string
}) {
  const { resolved } = useTheme()
  if (!data || !xKey || !yKey) {
    return <p className="text-xs text-gray-400 italic">LineChart: missing data/xKey/yKey</p>
  }
  return (
    <div className="space-y-1">
      {title && <p className="text-xs font-medium text-gray-500">{title}</p>}
      <ResponsiveContainer width="100%" height={200}>
        <RechartsLine data={data as Record<string, unknown>[]}>
          <CartesianGrid strokeDasharray="3 3" stroke={themeColor('--mc-gray-100')} />
          <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip contentStyle={tooltipStyle(resolved)} />
          <Line type="monotone" dataKey={yKey} stroke={themeColor('--mc-indigo-500')} strokeWidth={2} dot={false} />
        </RechartsLine>
      </ResponsiveContainer>
    </div>
  )
}

export function BarChart({
  data,
  xKey,
  yKey,
  title,
}: {
  data?: unknown[]
  xKey?: string
  yKey?: string
  title?: string
}) {
  const { resolved } = useTheme()
  if (!data || !xKey || !yKey) {
    return <p className="text-xs text-gray-400 italic">BarChart: missing data/xKey/yKey</p>
  }
  return (
    <div className="space-y-1">
      {title && <p className="text-xs font-medium text-gray-500">{title}</p>}
      <ResponsiveContainer width="100%" height={200}>
        <RechartsBar data={data as Record<string, unknown>[]}>
          <CartesianGrid strokeDasharray="3 3" stroke={themeColor('--mc-gray-100')} />
          <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip contentStyle={tooltipStyle(resolved)} />
          <Bar dataKey={yKey} fill={themeColor('--mc-indigo-500')} radius={[3, 3, 0, 0]} />
        </RechartsBar>
      </ResponsiveContainer>
    </div>
  )
}

export function Badge({ text, variant }: { text?: string; variant?: string }) {
  const variantClass: Record<string, string> = {
    success: 'bg-green-100 text-green-800',
    error:   'bg-red-100 text-red-800',
    warning: 'bg-amber-100 text-amber-800',
    info:    'bg-blue-100 text-blue-800',
  }
  const cls = variantClass[variant ?? ''] ?? 'bg-gray-100 text-gray-600'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {text ?? ''}
    </span>
  )
}

export function Alert({ variant, text }: { variant?: string; text?: string }) {
  const variantClass: Record<string, string> = {
    success: 'border-green-200 bg-green-50 text-green-700',
    error:   'border-red-200 bg-red-50 text-red-700',
    warning: 'border-amber-200 bg-amber-50 text-amber-700',
    info:    'border-blue-200 bg-blue-50 text-blue-700',
  }
  const cls = variantClass[variant ?? ''] ?? 'border-gray-200 bg-gray-50 text-gray-700'
  return (
    <div className={`rounded-lg border px-4 py-3 text-sm ${cls}`}>
      {text ?? ''}
    </div>
  )
}

// CATALOG map is in catalog.ts to satisfy fast-refresh (components-only file requirement).
